import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ExcelJS from 'exceljs';
import purchaseReportService from '../../../services/purchaseReportService';
import { supplierService } from '../../../services/supplierService';
import { DOCUMENT_TYPE_OPTIONS } from '../../../utils/documentTypes';
import { formatDateLocaleLima } from '../../../utils/dateUtils';
import Toast from '../../../components/Common/Toast';

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

const STATUS_OPTIONS = [
  { value: '', label: 'Todos los estados' },
  { value: 'issued', label: 'Emitidos' },
  { value: 'open', label: 'Abiertos' },
  { value: 'completed', label: 'Completados' },
  { value: 'cancelled', label: 'Cancelados' },
];

const formatMoney = (amount, currency = 'PEN') => {
  const value = parseFloat(amount) || 0;
  const symbol = currency === 'USD' ? 'US$' : 'S/';
  return `${symbol} ${value.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const DocumentRegister = () => {
  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const years = Array.from({ length: 8 }, (_, i) => now.getFullYear() - i);
  const [supplierId, setSupplierId] = useState('');
  const [documentType, setDocumentType] = useState('');
  const [status, setStatus] = useState('issued');
  const [currency, setCurrency] = useState('');
  const [search, setSearch] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [rows, setRows] = useState([]);
  const [totalsByCurrency, setTotalsByCurrency] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasQueried, setHasQueried] = useState(false);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  useEffect(() => {
    supplierService.getSuppliers({ active_only: true }).then((res) => {
      if (res.success && res.data) {
        setSuppliers(res.data);
      }
    }).catch(() => {});
  }, []);

  const buildParams = () => {
    const params = {
      year: selectedYear,
      month: selectedMonth,
    };
    if (supplierId) params.supplier_id = supplierId;
    if (documentType) params.document_type = documentType;
    if (status) params.status = status;
    if (currency) params.currency = currency;
    if (search.trim()) params.search = search.trim();
    return params;
  };

  const loadReport = async () => {
    setLoading(true);
    try {
      const response = await purchaseReportService.getDocumentRegister(buildParams());
      if (response.success) {
        setRows(response.data?.rows || []);
        setTotalsByCurrency(response.data?.totals_by_currency || []);
        setHasQueried(true);
      } else {
        setToast({
          isOpen: true,
          message: response.message || 'Error al cargar el reporte',
          type: 'error',
        });
      }
    } catch (error) {
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al cargar el registro de documentos',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = async () => {
    if (!rows.length) {
      setToast({ isOpen: true, message: 'No hay datos para exportar', type: 'warning' });
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Registro documentos');

    sheet.mergeCells('A1:N1');
    sheet.getCell('A1').value = `Registro de compras — ${MONTH_NAMES[selectedMonth - 1]} ${selectedYear}`;
    sheet.getCell('A1').font = { bold: true, size: 14 };

    sheet.addRow([]);
    const headerRow = sheet.addRow([
      'N°', 'Código', 'Tipo', 'Serie', 'Número', 'Proveedor', 'Doc. proveedor',
      'F. emisión', 'F. vencimiento', 'F. pago', 'Moneda', 'Base imponible', 'IGV', 'No gravados', 'Total', 'Condición pago', 'Estado',
    ]);
    headerRow.font = { bold: true };

    rows.forEach((row) => {
      sheet.addRow([
        row.row_number,
        row.code,
        row.document_type_label,
        row.series || '',
        row.number || '',
        row.supplier_name || '',
        row.supplier_document || '',
        row.issue_date ? formatDateLocaleLima(row.issue_date) : '',
        row.due_date ? formatDateLocaleLima(row.due_date) : '',
        row.payment_date ? formatDateLocaleLima(row.payment_date) : '',
        row.currency || '',
        row.base_imponible,
        row.igv_amount,
        row.no_gravados,
        row.total_amount,
        row.payment_condition || '',
        row.status_label || '',
      ]);
    });

    totalsByCurrency.forEach((total) => {
      sheet.addRow([]);
      sheet.addRow([
        '', '', '', '', '', '', '', '', '', '', `Totales ${total.currency}`,
        total.base_imponible, total.igv_amount, total.no_gravados, total.total_amount,
      ]);
    });

    sheet.columns.forEach((col) => {
      col.width = 14;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `registro-documentos-${selectedYear}-${String(selectedMonth).padStart(2, '0')}.xlsx`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-6">
        <div className="max-w-[1400px] mx-auto">
          <div className="mb-6">
            <nav className="text-sm text-gray-600 mb-3">
              <Link to="/dashboard" className="hover:text-teal-600">Dashboard</Link>
              <span className="mx-2">/</span>
              <Link to="/purchase-orders" className="hover:text-teal-600">Compras</Link>
              <span className="mx-2">/</span>
              <Link to="/compras/reports" className="hover:text-teal-600">Reportes</Link>
              <span className="mx-2">/</span>
              <span className="text-gray-800">Registro de documentos</span>
            </nav>

            <div className="flex flex-wrap justify-between items-start gap-4 mb-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-800">Registro de documentos</h1>
                <p className="text-gray-600 mt-1">
                  Registro de compras por cabecera de documento (sin detalle de ítems).
                </p>
              </div>
              <button
                type="button"
                onClick={exportToExcel}
                disabled={!rows.length}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-md text-sm font-medium"
              >
                Exportar Excel
              </button>
            </div>

            <div className="bg-white rounded-lg shadow-md p-5 mb-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Mes de emisión</label>
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(parseInt(e.target.value, 10))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500"
                  >
                    {MONTH_NAMES.map((name, index) => (
                      <option key={name} value={index + 1}>{name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Año de emisión</label>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500"
                  >
                    {years.map((year) => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Proveedor</label>
                  <select
                    value={supplierId}
                    onChange={(e) => setSupplierId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="">Todos</option>
                    {suppliers.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Estado</label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500"
                  >
                    {STATUS_OPTIONS.map((opt) => (
                      <option key={opt.value || 'all'} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Tipo documento</label>
                  <select
                    value={documentType}
                    onChange={(e) => setDocumentType(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="">Todos</option>
                    {DOCUMENT_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                    <option value="suministro_continuo">Suministro continuo</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Moneda</label>
                  <select
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="">Todas</option>
                    <option value="PEN">PEN</option>
                    <option value="USD">USD</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Buscar</label>
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Código, serie, número o proveedor..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500"
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={loadReport}
                disabled={loading}
                className="px-5 py-2 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white rounded-md font-medium"
              >
                {loading ? 'Consultando...' : 'Consultar'}
              </button>
            </div>

            {totalsByCurrency.length > 0 && (
              <div className="space-y-4 mb-6">
                {totalsByCurrency.map((total) => (
                  <div
                    key={total.currency}
                    className="bg-white rounded-lg shadow-md px-6 py-5 w-full border border-gray-100"
                  >
                    <div className="text-sm font-medium text-gray-600 mb-4">
                      Totales {total.currency}
                      <span className="font-normal text-gray-500">
                        {' '}· {total.count} documento{total.count === 1 ? '' : 's'}
                        {' '}· {MONTH_NAMES[selectedMonth - 1]} {selectedYear}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      <div className="rounded-lg bg-gray-50 px-4 py-3">
                        <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Base imponible</div>
                        <div className="text-lg font-semibold text-gray-900">
                          {formatMoney(total.base_imponible, total.currency)}
                        </div>
                      </div>
                      <div className="rounded-lg bg-gray-50 px-4 py-3">
                        <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">IGV</div>
                        <div className="text-lg font-semibold text-gray-900">
                          {formatMoney(total.igv_amount, total.currency)}
                        </div>
                      </div>
                      <div className="rounded-lg bg-gray-50 px-4 py-3">
                        <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">No gravado</div>
                        <div className="text-lg font-semibold text-gray-900">
                          {formatMoney(total.no_gravados, total.currency)}
                        </div>
                      </div>
                      <div className="rounded-lg bg-teal-50 px-4 py-3 border border-teal-100">
                        <div className="text-xs text-teal-700 uppercase tracking-wide mb-1">Total</div>
                        <div className="text-xl font-bold text-teal-800">
                          {formatMoney(total.total_amount, total.currency)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {!hasQueried ? (
            <div className="bg-white rounded-lg shadow-md p-12 text-center text-gray-500">
              Selecciona el mes y año de emisión y pulsa Consultar para ver el registro.
            </div>
          ) : loading ? (
            <div className="bg-white rounded-lg shadow-md p-12 text-center text-gray-500">
              Cargando registro...
            </div>
          ) : rows.length === 0 ? (
            <div className="bg-white rounded-lg shadow-md p-12 text-center text-gray-500">
              No hay documentos con los filtros seleccionados.
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-md overflow-x-auto">
              <div className="px-4 py-3 border-b border-gray-100 text-sm font-medium text-gray-700">
                {MONTH_NAMES[selectedMonth - 1]} {selectedYear}
                <span className="text-gray-500 font-normal ml-2">({rows.length} registro{rows.length === 1 ? '' : 's'})</span>
              </div>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">N°</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Serie-Número</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Proveedor</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">F. emisión</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">F. pago</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Moneda</th>
                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Base</th>
                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">IGV</th>
                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">No gravado</th>
                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map((row) => {
                    const serieNumero = row.series && row.number
                      ? `${row.series}-${row.number}`
                      : (row.series || row.number || '—');
                    return (
                      <tr key={row.id} className="hover:bg-gray-50">
                        <td className="px-3 py-3 text-sm text-gray-500">{row.row_number}</td>
                        <td className="px-3 py-3 text-sm font-mono text-teal-700">{row.code}</td>
                        <td className="px-3 py-3 text-sm text-gray-900">{row.document_type_label}</td>
                        <td className="px-3 py-3 text-sm text-gray-700">{serieNumero}</td>
                        <td className="px-3 py-3 text-sm">
                          <div className="font-medium text-gray-900">{row.supplier_name || '—'}</div>
                          {row.supplier_document && (
                            <div className="text-xs text-gray-500">{row.supplier_document}</div>
                          )}
                        </td>
                        <td className="px-3 py-3 text-sm text-gray-600">
                          {row.issue_date ? formatDateLocaleLima(row.issue_date) : '—'}
                        </td>
                        <td className="px-3 py-3 text-sm text-gray-600">
                          {row.payment_date ? formatDateLocaleLima(row.payment_date) : '—'}
                        </td>
                        <td className="px-3 py-3 text-sm text-gray-700">{row.currency}</td>
                        <td className="px-3 py-3 text-sm text-right text-gray-900">
                          {formatMoney(row.base_imponible, row.currency)}
                        </td>
                        <td className="px-3 py-3 text-sm text-right text-gray-900">
                          {formatMoney(row.igv_amount, row.currency)}
                        </td>
                        <td className="px-3 py-3 text-sm text-right text-gray-900">
                          {formatMoney(row.no_gravados, row.currency)}
                        </td>
                        <td className="px-3 py-3 text-sm text-right font-medium text-gray-900">
                          {formatMoney(row.total_amount, row.currency)}
                        </td>
                        <td className="px-3 py-3 text-sm">
                          <span className={`px-2 py-0.5 text-xs rounded-full ${
                            row.status === 'issued'
                              ? 'bg-green-100 text-green-800'
                              : row.status === 'cancelled'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-gray-100 text-gray-700'
                          }`}
                          >
                            {row.status_label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default DocumentRegister;
